import {createSelector} from "reselect";
import DataManager from "../DataManagers/DataManager";
import {fieldIsWithinBounds, numSort} from "../utilsData";
import {
	ACS_DATA_NAME_ACCESSOR,
	FILTER_TYPE,
	GEO_ID_ACCESSOR,
	LEGEND_TYPE,
	NATIONAL_KEY,
	STATE_NAME_ACCESSOR
} from "../constants";
import {createSteps} from "../utils";
import RURAL_DEFINITION from "../content/rural-definition/rural-definition";
import {scaleLinear} from "d3-scale";
import {FULL_STATE_NAME_TO_ABBREVIATION} from "../DataManagers/utilsColorTheme";

// SELECTORS **************************************************

// 0. LAMBDAS
export const stateSelector = state => state
export const cartoACSDataSelector = state => state.cartoACSData
export const mapSelector = state => state.map
export const mapboxLayersSelector = state => state.mapboxLayers
export const uiSelector = state => state.ui

// MAP SELECTORS ****************

// 1. Non-exported

export const viewportSelector = createSelector(
	mapSelector,
	map => map.viewport
)
export const settingsSelector = createSelector(
	mapSelector,
	map => map.settings
)
export const currentSceneIndexSelector = createSelector(
	mapSelector,
	map => map.currentSceneIndex
)
export const visibleLayersSelector = createSelector(
	mapSelector,
	map => map.visibleLayers
)
export const initialLoadOfMapHasCompletedSelector = createSelector(
	mapSelector,
	map => map.initialLoadOfMapHasCompleted
)
export const storyMapModeSelector = createSelector(
	mapSelector,
	map => map.storyMapMode
)
export const presenterModeActiveSelector = createSelector(
	mapSelector,
	map => map.presenterModeActive
)
export const showStartScreenSelector = createSelector(
	mapSelector,
	map => map.showStartScreen
)
export const _isTransitioningSelector = createSelector(
	mapSelector,
	map => map._isTransitioning
)
export const initialZoomInFromStartScreenCompleteSelector = createSelector(
	mapSelector,
	map => map.initialZoomInFromStartScreenComplete
)
export const _turfManagerSelector = createSelector(
	mapSelector,
	map => map._turfManager
)
export const selectedRegionInfoSelector = createSelector(
	mapSelector,
	map => map.selectedRegionInfo
)
export const comparisonRegionInfoSelector = createSelector(
	mapSelector,
	map => map.comparisonRegionInfo
)
export const userSetHideLayersSelector = createSelector(
	mapSelector,
	map => map.userSetHideLayers
)
export const geoIdForComparisonRegionSelector = createSelector(
	comparisonRegionInfoSelector,
	comparisonRegionInfo => comparisonRegionInfo.geoIdForSelectedRegion
)
export const dynamicMapboxFiltersSelector = createSelector(
	mapSelector,
	map => map.dynamicMapboxFilters
)
export const activeMapboxFiltersSelector = createSelector(
	mapSelector,
	map => map.activeMapboxFilters
)
export const clickedLayersInfoSelector = createSelector(
	mapSelector,
	map => map.clickedLayersInfo
)
export const hoveredLayersInfoSelector = createSelector(
	mapSelector,
	map => map.hoveredLayersInfo || {}
)
export const hoveredCartoFeaturesSelector = createSelector(
	hoveredLayersInfoSelector,
	hoveredLayersInfo => hoveredLayersInfo.hoveredCartoFeatures || []
)
export const mouseHoverCoordsSelector = createSelector(
	hoveredLayersInfoSelector,
	hoveredLayersInfo => hoveredLayersInfo.mouseHoverCoords || {}
)
export const rawStorymapSelector = createSelector(
	mapSelector,
	map => map.rawStorymap
)
export const mapStyleSelector = createSelector(
	mapSelector,
	map => map.mapStyle
)

export const selectedRuralDefinitionSelector = createSelector(
	mapboxLayersSelector,
	mapboxLayers => mapboxLayers.selectedRuralDefinition
)
export const isLoadingCartoExtentsSelector = createSelector(
	cartoACSDataSelector,
	cartoACSData => cartoACSData.isLoadingCartoExtents
)
export const selectedUSStatesToFilterWithinSelector = createSelector(
	cartoACSDataSelector,
	cartoACSData => cartoACSData.selectedUSStatesToFilterWithin
)
export const arrayOfUSStatesLoadingACSDataSelector = createSelector(
	cartoACSDataSelector,
	cartoACSData => cartoACSData.arrayOfUSStatesLoadingACSData
)
export const currentlyLoadingACSDataSelector = createSelector(
	arrayOfUSStatesLoadingACSDataSelector,
	arrayOfUSStatesLoadingACSData => arrayOfUSStatesLoadingACSData.length > 0
)
export const errorMessagesSelector = createSelector(
	cartoACSDataSelector,
	cartoACSData => cartoACSData.errorMessages
)
export const fieldFilterBoundsSelector = createSelector(
	dynamicMapboxFiltersSelector,
	(dynamicMapboxFilters) => dynamicMapboxFilters.fieldFilterBounds,
)
export const userHasDraggedToNewLocationSelector = createSelector(
	mapSelector,
	(map) => map.userHasDraggedToNewLocation,
)
export const showSatelliteLayerSelector = createSelector(
	mapSelector,
	map => map.showSatelliteLayer,
)
export const showPrimaryFilterSelector = createSelector(
	mapSelector,
	map => map.showPrimaryFilter,
)
export const _mapRefSelector = createSelector(
	mapSelector,
	map => map._mapRef || {},
)

export const getSmallMapSelector = (mapState) => {
	// Write state to URL, excluding properties which are HUGE (eg mapStyle) and/or unneeded.
	const {
		mapStyle, clickedLayersInfo, hoveredLayersInfo, settings, defaultMapStyle, rawStorymap, _mapRef, _prev, _isTransitioning, initialLoadOfMapHasCompleted,
		_turfManager, dynamicMapboxFilters, activeMapboxFilters, allMapboxFilters,
		storyMapMode, showStartScreen, presenterModeActive,
		viewport, visibleLayers,
		..._smallMapState
	} = mapState
	
	const {latitude, longitude, zoom} = viewport
	
	// Shorten link by just storing ACTIVE layers.
	// TODO: this is only useful if users can toggle on/off layers. In which case we could just store diff(activeLayers, sceneLayers)
	// const _visibleLayers = Object.keys(visibleLayers).reduce((justActiveLayers, cartoLayerId) => {
	// 	if (visibleLayers[cartoLayerId] === true) justActiveLayers[cartoLayerId] = true
	// 	return justActiveLayers
	// }, {})
	
	return {
		..._smallMapState,
		// visibleLayers: _visibleLayers,
		viewport: {latitude, longitude, zoom},
	}
}

// UI SELECTORS

export const optSelector = createSelector(
	uiSelector,
	ui => ui.opt,
)
export const filterFieldBeingManuallyEditedSelector = createSelector(
	uiSelector,
	ui => ui.filterFieldBeingManuallyEdited,
)
export const featureTourIsOpenSelector = createSelector(
	uiSelector,
	ui => ui.featureTourIsOpen,
)
export const filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTERSelector = createSelector(
	fieldFilterBoundsSelector,
	filterFieldBeingManuallyEditedSelector,
	// This just gets first filter (could be multiple). We want to move away from primaryFilter paradigm longer term...
	(fieldFilterBounds, filterFieldBeingManuallyEdited) => filterFieldBeingManuallyEdited || Object.keys(fieldFilterBounds).find(fieldName => _filterIsActive(fieldFilterBounds[fieldName]))
)


// CARTO ACS DATA SELECTORS ****************

// 1. Direct lambda-using selectors
// 0. LAMBDAS
export const _createSummaryFromData = (raw) => {
	const summary = {}
	raw.forEach(d => {
		const stateName = d[STATE_NAME_ACCESSOR]
		
		if (!(stateName in summary)) {
			const props = Object.keys(d).filter(k => k !== STATE_NAME_ACCESSOR)
			const st_props = props.filter(k => k.substring(0, 3) === 'st_')
			summary[stateName] = st_props.reduce((s, k) => {
				s[k] = d[k];
				return s;
			}, {})
			
			if (!(NATIONAL_KEY in summary)) {
				const us_props = props.filter(k => k.substring(0, 3) === 'us_')
				summary[NATIONAL_KEY] = us_props.reduce((s, k) => {
					s[k] = d[k];
					return s;
				}, {})
			}
		}
	})
	return summary
}

export const stateNameAbbreviatedSelector = (ACSDataRow) => ACSDataRow[STATE_NAME_ACCESSOR] ? FULL_STATE_NAME_TO_ABBREVIATION[ACSDataRow[STATE_NAME_ACCESSOR]] : ''

export const selectedGeographySelector = createSelector(
	cartoACSDataSelector,
	(cartoACSData) => cartoACSData.selectedGeography,
)
export const cachedDataSelector = createSelector(
	cartoACSDataSelector,
	selectedGeographySelector,
	(cartoACSData, selectedGeography) => cartoACSData[selectedGeography.cartoLayerId] || {},
)
export const ACSSummaryDataForEntireUSSelector = createSelector(
	cachedDataSelector,
	(cachedData) => cachedData.summary || {},
)
export const nationalSummarySelector = createSelector(
	ACSSummaryDataForEntireUSSelector,
	ACSSummaryDataForEntireUS => ACSSummaryDataForEntireUS[NATIONAL_KEY] || {},
)
export const ACSDataForStatesSelector = createSelector(
	selectedUSStatesToFilterWithinSelector,
	cachedDataSelector,
	(selectedUSStatesToFilterWithin, cachedData) => selectedUSStatesToFilterWithin.reduce((combinedData, stateName) => {
		// console.log('....ACSDataForStatesSelector....')
		const ACSDataForStates = cachedData[stateName] || []
		return [...combinedData, ...ACSDataForStates]
	}, [])
)
export const getCachedACSDataAndSummaryForStatesSelector = createSelector(
	ACSDataForStatesSelector,
	ACSSummaryDataForEntireUSSelector,
	(ACSDataForStates, ACSSummaryDataForEntireUS) => ({ACSDataForStates, ACSSummaryDataForEntireUS}),
)
export const cartoLayerIdOfSelectedGeographySelector = createSelector(
	selectedGeographySelector,
	selectedGeography => selectedGeography.cartoLayerId
)
export const getSmallCartoACSDataSelector = cartoACSData => {
	const {selectedGeography, selectedUSStatesToFilterWithin} = cartoACSData
	return {
		selectedGeography,
		selectedUSStatesToFilterWithin,
	}
}

// ROOT SELECTORS ****************
export const _filterIsActive = fieldFilterBoundsObject => fieldFilterBoundsObject.hasBeenEditedByTheUser || fieldFilterBoundsObject.buttonToggleStatusArrayForField.find(isActive => isActive)
export const filterIsActive = (fieldName, fieldFilterBounds) => fieldName && _filterIsActive(fieldFilterBounds[fieldName])
export const numActiveFilters = fieldFilterBounds => Object.values(fieldFilterBounds).reduce((numActiveFilters, currentFilter) => numActiveFilters + (_filterIsActive(currentFilter) ? 1 : 0), 0)
export const activeFilterFieldNamesSelector = createSelector(
	fieldFilterBoundsSelector,
	fieldFilterBounds => Object.keys(fieldFilterBounds).filter(currentFilterFieldName => filterIsActive(currentFilterFieldName, fieldFilterBounds)),
)
export const numActiveFiltersSelector = createSelector(
	activeFilterFieldNamesSelector,
	activeFilterFieldNames => activeFilterFieldNames.length,
)
export const applyAllFiltersSelector = createSelector(
	numActiveFiltersSelector,
	numActiveFilters => numActiveFilters > 1,
)
export const showFiltersOnMapSelector = createSelector(
	showPrimaryFilterSelector,
	numActiveFiltersSelector,
	(showPrimaryFilter, numActiveFilters) => showPrimaryFilter && !!numActiveFilters,
)

export const filteredACSDataForStatesSelector = createSelector(
	ACSDataForStatesSelector,
	fieldFilterBoundsSelector,
	currentSceneIndexSelector,
	filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTERSelector,
	(ACSDataForStates, fieldFilterBounds, currentSceneIndex, filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER) => {
		// console.log('.....filteredACSDataForStatesSelector: FILTER & SORT')
		return Object.keys(fieldFilterBounds)
			.filter(fieldName => _filterIsActive(fieldFilterBounds[fieldName]))
			.reduce( // use reduce for style + efficiency <3
				(filteredACSData, filteredFieldName) => filteredACSData.filter(region => fieldIsWithinBounds(region, fieldFilterBounds, filteredFieldName)),
				ACSDataForStates // Begin with all the data
			)
			.sort((a, b) => numSort(a, b, filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER))
	}
)
export const geoIdForSelectedRegionSelector = createSelector(
	selectedRegionInfoSelector,
	filteredACSDataForStatesSelector,
	ACSDataForStatesSelector,
	(selectedRegionInfo, filteredACSDataForStates, ACSDataForStates) => {
		const arbitraryRegionDataRowIfAny = filteredACSDataForStates[0] || ACSDataForStates[0] || {}
		return selectedRegionInfo.geoIdForSelectedRegion || arbitraryRegionDataRowIfAny[GEO_ID_ACCESSOR]
		
	}
)
export const selectedRegionACSDataRowSelector = createSelector(
	ACSDataForStatesSelector,
	geoIdForSelectedRegionSelector,
	currentSceneIndexSelector,
	(ACSDataForStates, geoIdForSelectedRegion, currentSceneIndex) => {
		const _selectedRegionACSDataRow = ACSDataForStates.find(regionRow => regionRow.geoid === geoIdForSelectedRegion) || {}
		
		const communitySummaryFieldNames = DataManager.communitySummaryFieldNamesAtIndex(currentSceneIndex)
		const isAFieldNotCurrentlyBeingShown = (fieldName) => (fieldName in DataManager.stats.getAllACSNumericFieldInLookupTable()) && !communitySummaryFieldNames.includes(fieldName)

		return Object.keys(_selectedRegionACSDataRow).reduce((filteredData, currentFieldName) => {
				return isAFieldNotCurrentlyBeingShown(currentFieldName)
					? filteredData // omit it
					: {
						...filteredData,
						[currentFieldName]: _selectedRegionACSDataRow[currentFieldName]
					}
			},
			{}
		)
	}
)
export const communitySummaryFieldsSelector = createSelector(
	currentSceneIndexSelector,
	(currentSceneIndex) => DataManager.communitySummaryFieldsAtIndex(currentSceneIndex)
)

export const comparisonRegionACSDataRowSelector = createSelector(
	filteredACSDataForStatesSelector,
	geoIdForComparisonRegionSelector,
	(filteredACSDataForStates, geoIdForSelectedRegion) => filteredACSDataForStates.find(regionRow => regionRow.geoid === geoIdForSelectedRegion) || {}
)
export const fieldNamesThatHaveBeenEditedByUserSelector = createSelector(
	fieldFilterBoundsSelector,
	(fieldFilterBounds) => Object.keys(fieldFilterBounds).filter(fieldName => fieldFilterBounds[fieldName].hasBeenEditedByTheUser)
)

// MAPBOX LAYERS SELECTORS ****************
export const getSmallMapboxLayersSelector = mapboxLayers => {
	const {selectedFilterOptionIndexesForEachRuralDefinition, selectedRuralDefinition} = mapboxLayers
	return {
		selectedRuralDefinition,
		selectedFilterOptionIndexesForEachRuralDefinition: {
			// Just store the currently selected definition (other are unlikely to be used)
			[selectedRuralDefinition]: selectedFilterOptionIndexesForEachRuralDefinition[selectedRuralDefinition],
		},
	}
}
export const getRuralFilterOptionIndexFromStringSelector = (selectedRuralDefinition, filterOptionString) => {
	return RURAL_DEFINITION[selectedRuralDefinition].allFilterOptions.indexOf(filterOptionString)
}

export const isRural = (selectedRuralDefinition, filterOptionString) => {
	const ruralFilterOptionIndexFromString = getRuralFilterOptionIndexFromStringSelector(selectedRuralDefinition, filterOptionString)
	const isUrban = RURAL_DEFINITION[selectedRuralDefinition].defaultFilterOptionsArrayIndex.includes(ruralFilterOptionIndexFromString)
	return !isUrban
}

export const smallReduxStateForURLSelector = (state) => {
	return {
		map: getSmallMapSelector(state.map),
		cartoACSData: getSmallCartoACSDataSelector(state.cartoACSData),
		mapboxLayers: getSmallMapboxLayersSelector(state.mapboxLayers),
	}
}

export const selectedFilterOptionIndexesForEachRuralDefinitionSelector = createSelector(
	mapboxLayersSelector,
	(mapboxLayers) => mapboxLayers.selectedFilterOptionIndexesForEachRuralDefinition
)
export const getSelectedFilterOptionStringsSelector = createSelector(
	selectedFilterOptionIndexesForEachRuralDefinitionSelector,
	selectedRuralDefinitionSelector,
	(selectedFilterOptionIndexesForEachRuralDefinition, selectedRuralDefinition) => selectedFilterOptionIndexesForEachRuralDefinition[selectedRuralDefinition].map(index => RURAL_DEFINITION[selectedRuralDefinition].allFilterOptions[index])
)
const _generateThePrimaryFilter = (currentSceneIndex, cartoLayerIdOfSelectedGeography, fieldExtents, fieldFilterBounds, filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER = null, categoricalFields = {}, filterType = FILTER_TYPE.NUMERIC) => {
	const primary = {
		layer: cartoLayerIdOfSelectedGeography || "attr_county",
		field: filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER || "avg_commute_in_minutes_2017",
		label: filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER || "Average Commute (Minutes)",
		type: filterType || FILTER_TYPE.NUMERIC
	}
	
	const colorTheme = DataManager.getVizColorThemeForCurrentSlide(currentSceneIndex)
	var categories = [] // TODO: a relic of trying to support categorical. Either support it, or delete all traces.
	
	const fieldBounds = fieldExtents[primary.field] || []
	const tinyOffset = Math.abs(fieldBounds[1] - fieldBounds[0]) / 20
	const paintSteps = createSteps({numSteps: 6, min: fieldBounds[0] - tinyOffset, max: fieldBounds[1] + tinyOffset})
	
	return {
		filterColors: colorTheme,
		filterSteps: paintSteps,
		fieldBounds,
		filterBounds: fieldFilterBounds[primary.field] && fieldFilterBounds[primary.field].filterBounds,
		legendType: LEGEND_TYPE.BINS,
		filterType: primary.type,
		filterCategories: categories,
		fieldName: filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER,
		fieldLabel: DataManager.stats.getDisplayName(primary.layer, filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER)
	}
}


export const fieldExtentsSelector = createSelector(
	dynamicMapboxFiltersSelector,
	dynamicMapboxFilters => dynamicMapboxFilters.fieldExtentsCache
)


// UTIL DISPLAY IMPORTS (DATA WRANGLING)

// Breaks Carto Data from the form it comes from the DB down into the point-per-fieldName-per-county form for the vis.
// Because all points in the Parallel Coords Chart must share a common y-axis (range), we scale them to a shared range.
export const RANGE_FOR_SCALED_VALUES = [0, 100]

export const columnWiseScaledCartoACSDataForVizSelector = createSelector(
	getCachedACSDataAndSummaryForStatesSelector,
	fieldExtentsSelector,
	({ACSDataForStates}, fieldExtents) => {
		if(Object.keys(fieldExtents).length === 0) return []
 		const columnWiseScaledCartoACSDataForViz = []
		ACSDataForStates.forEach(regionRow => {
			const extractedData = DataManager.stats.getAllACSNumericFieldNames().reduce((arrayOfExtractedData, fieldName,) => {
				const _rangeAccessor = regionRow[fieldName]
				if (_rangeAccessor !== null) { // Skip points with null range values (this breaks line, but keeps good data points)
					const locationNameCommaState = `${regionRow['namelsad']}, ${regionRow['st_name']}`
					const locationKeyWithoutFieldName = `${locationNameCommaState}: ${regionRow[GEO_ID_ACCESSOR]}`
					arrayOfExtractedData.push({
						ordinalAccessor: fieldName,
						connectedLineGroup: regionRow[ACS_DATA_NAME_ACCESSOR],
						rangeAccessor: _rangeAccessor,
						row: regionRow,
						// renderKey allows us to track/smoothly transition data points within a geography
						// Swarm's render-keys exclude fieldName since we're visualizing 1 fieldName at a time, and we want them to transition between
						swarmRenderKey: locationKeyWithoutFieldName,
						// Include field  name to avoid collision since we're showing multiple fields at once
						parallelCoordinatesRenderKey: `${fieldName} for ${locationKeyWithoutFieldName}`,
					})
					
				}
				return arrayOfExtractedData
			}, [])
			columnWiseScaledCartoACSDataForViz.push(...extractedData)
		})
		
		const columnWiseScaledCartoACSDataForViz_FILTERED = columnWiseScaledCartoACSDataForViz.filter(({rangeAccessor}) => isFinite(rangeAccessor) && !Number.isNaN(rangeAccessor))
		const linear = scaleLinear().range(RANGE_FOR_SCALED_VALUES)
		
		columnWiseScaledCartoACSDataForViz_FILTERED.forEach(d => {
			const _linear = linear.domain(fieldExtents[d.ordinalAccessor])
			d.scaledRangeAccessor = _linear(d.rangeAccessor)
		})
		
		return columnWiseScaledCartoACSDataForViz_FILTERED
	}
)

// export const fieldExtentsFromColumnWiseScaledCartoACSDataSelector = createSelector(
// 	columnWiseScaledCartoACSDataForVizSelector,
// 	(columnWiseScaledCartoACSDataForViz) => {
// 		const fieldExtents = {}
// 		Object.values(ACS_NUMERIC_FIELDS).forEach(fieldName => {
// 			const arrayOfIndicatorValues = columnWiseScaledCartoACSDataForViz.filter(d => d.ordinalAccessor === fieldName).map(d => d.rangeAccessor)
// 			fieldExtents[fieldName] = d3Extent(arrayOfIndicatorValues)
// 		})
//
// 		return fieldExtents
// 	}
// )

export const scaleFilterBounds = (fieldFilterBounds, fieldExtents) => {
	const scaledFieldFilterBounds = {}
	Object.keys(fieldExtents).forEach(fieldName => {
		const filterBounds = fieldFilterBounds[fieldName].filterBounds
		const extent = fieldExtents[fieldName]
		const scale = scaleLinear().range(RANGE_FOR_SCALED_VALUES).domain(extent)
		scaledFieldFilterBounds[fieldName] = filterBounds ? filterBounds.map(num => scale(num)) : extent.map(num => scale(num))
	})
	return scaledFieldFilterBounds
}
export const convertScaledBoundsToOriginalValue = (scaledFieldFilterBounds, fieldExtents) => {
	const originalValues = {}
	Object.keys(fieldExtents).forEach(fieldName => {
		// SEMIOTIC.JS sets bounds to null if you click outside the brushes, defaulting the brush to the full range
		const filterBounds = scaledFieldFilterBounds[fieldName] || RANGE_FOR_SCALED_VALUES
		const extent = fieldExtents[fieldName]
		const scale = scaleLinear().domain(RANGE_FOR_SCALED_VALUES).range(extent)
		originalValues[fieldName] = filterBounds.map(num => scale(num))
	})
	return originalValues
}

export const buttonToggleStatusArrayForFieldSelector = (fieldName, fieldFilterBounds) => fieldFilterBounds[fieldName] ? fieldFilterBounds[fieldName].buttonToggleStatusArrayForField : []
export const hasBeenEditedByTheUserSelector = (fieldName, fieldFilterBounds) => fieldFilterBounds[fieldName] && fieldFilterBounds[fieldName].hasBeenEditedByTheUser

export const primaryFilterSelector = createSelector(
	currentSceneIndexSelector,
	cartoLayerIdOfSelectedGeographySelector,
	fieldExtentsSelector,
	fieldFilterBoundsSelector,
	filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTERSelector,
	
	(currentSceneIndex,
	 cartoLayerIdOfSelectedGeography,
	 fieldExtents,
	 fieldFilterBounds,
	 filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER,) => _generateThePrimaryFilter(currentSceneIndex, cartoLayerIdOfSelectedGeography, fieldExtents, fieldFilterBounds, filterFieldBeingManuallyEdited_ELSE_THE_FIRST_ACTIVE_FILTER)
)

// TODO: REFACTOR code to just use primaryFilterFieldNameSelector
export const primaryFilterFieldNameInAnArraySelector = createSelector(
	primaryFilterSelector,
	primaryFilter => primaryFilter.fieldName ? [primaryFilter.fieldName] : []
)

export const primaryFilterFieldNameSelector = createSelector(
	primaryFilterSelector,
	primaryFilter => primaryFilter.fieldName
)

export const primaryFilterStepsSelector = createSelector(
	primaryFilterSelector,
	primaryFilter => primaryFilter.filterSteps
)

export const primaryFilterColorsSelector = createSelector(
	primaryFilterSelector,
	primaryFilter => primaryFilter.filterColors
)
